import React, { useState } from 'react';
import PageContainer from '../components/Layout/PageContainer';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import { MessageSquare, Send, Bot, User, Lightbulb, AlertTriangle } from 'lucide-react';

const Advisor = () => {
  const [messages, setMessages] = useState([
    { id: 1, role: 'assistant', content: "Hello! I'm your AI Security Advisor. Ask me about threats, vulnerabilities, or recommended actions.", timestamp: new Date() },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = { id: Date.now(), role: 'user', content: input, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    setTimeout(() => {
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I've analyzed your request. Based on current threat intelligence, here are my recommendations...",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setLoading(false);
    }, 1500);
  };

  const suggestions = [
    'What are the current top threats?',
    'Show me assets with critical vulnerabilities',
    'Generate a security report',
    'Recommend priority actions',
  ];

  return (
    <PageContainer title="AI Advisor" subtitle="LLM-powered security guidance and recommendations">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 flex flex-col h-[600px]">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-start space-x-2 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'assistant' ? 'bg-primary' : 'bg-slate-200'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <Bot className="w-4 h-4 text-white" />
                    ) : (
                      <User className="w-4 h-4 text-slate-600" />
                    )}
                  </div>
                  <div className={`p-3 rounded-lg ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-slate-50 text-slate-900'}`}>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center space-x-2 text-slate-500">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask about security threats, vulnerabilities, or recommendations..."
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
              <Button onClick={handleSend} disabled={loading || !input.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          <Card>
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
              <Lightbulb className="w-4 h-4 mr-2 text-warning" />
              Suggestions
            </h3>
            <div className="space-y-2">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => setInput(suggestion)}
                  className="w-full text-left p-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2 text-danger" />
              Active Recommendations
            </h3>
            <div className="space-y-3">
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-sm font-medium text-danger">Critical: Block 192.168.1.105</p>
                <p className="text-xs text-slate-500 mt-1">Multiple failed SSH attempts detected</p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                <p className="text-sm font-medium text-warning">Review: Outdated OpenSSL</p>
                <p className="text-xs text-slate-500 mt-1">3 assets running vulnerable version</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
};

export default Advisor;
